import type { FlagStepGate } from 'definitions';
import type { DurationUnit, StepDraft } from '@/state/flags/flag-drafts';
import type { FlagManager } from '@/state/flags/useFlag';
import type { PlanEditorManager } from '@/state/flags/usePlanEditor';
import type { PlanStartPreview } from '@/state/flags/flag-status';
import { FLAG_STEP_GATES } from 'definitions';
import { Pause, Play, Plus, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { usesStepTime } from '@/state/flags/flag-drafts';
import { percentOf } from '@/state/flags/flag-status';
import { usePlanEditor } from '@/state/flags/usePlanEditor';
import { PlanStatusChip } from '../FlagChips';
import { FieldLabel, FlagSection, FIELD_CLASS, SaveStrip, SECONDARY_BUTTON_CLASS } from './FlagSection';
import { FLAG_COPY } from '../copy';

type RolloutPlanProps = { manager: FlagManager };

const COPY = FLAG_COPY.plan;

/**
 * Wide-screen columns: number, share, what moves it on, time on the step, not-before date,
 * remove. From lg, since six columns do not fit beside the sidebar at md.
 */
const STEP_GRID =
  'lg:grid lg:grid-cols-[28px_110px_minmax(0,180px)_minmax(0,210px)_minmax(0,220px)_36px] lg:items-center lg:gap-3';

/**
 * The steps the rollout climbs on its own: a share, what lets it move on (its time, the
 * guardrail, or both), the least time at it and the earliest date to move on. The guardrail's
 * margin is set here once a step uses it, and the holdback before publishing. Why a plan is
 * paused is explained once, on the status card; here it is a chip.
 */
export const RolloutPlan = ({ manager }: RolloutPlanProps) => {
  const plan = usePlanEditor(manager, COPY);
  const showsCurrent = plan.planStatus === 'active' || plan.planStatus === 'paused';
  const startLabel = plan.planStatus === 'paused' ? COPY.resume : COPY.start;

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-plan"
      actions={
        // Nothing to start or pause until a plan is saved.
        plan.hasPlan ? (
          <>
            <PlanStatusChip status={plan.planStatus} byGuardrail={plan.pausedByGuardrail} />
            {plan.canPause && (
              <Button type="button" variant="outline" onClick={plan.pause} className={SECONDARY_BUTTON_CLASS}>
                <Pause className="size-3.5" strokeWidth={1.5} />
                {COPY.pause}
              </Button>
            )}
            {manager.isLive && !plan.isActive && (
              <Button
                type="button"
                variant="outline"
                onClick={plan.askStart}
                disabled={!plan.canStart}
                className={SECONDARY_BUTTON_CLASS}
              >
                <Play className="size-3.5" strokeWidth={1.5} />
                {startLabel}
              </Button>
            )}
          </>
        ) : undefined
      }
      footer={
        plan.isDirty ? (
          <SaveStrip
            blockingReason={plan.blockingReason}
            saveLabel={COPY.save}
            discardLabel={COPY.discard}
            onSave={plan.save}
            onDiscard={plan.discard}
            isSaving={plan.isSaving}
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {plan.steps.length === 0 ? (
          <p className="supporting m-0">{COPY.empty}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className={cn('hidden', STEP_GRID)}>
              <span className="micro-label" aria-hidden="true">
                #
              </span>
              <FieldLabel label={COPY.share} />
              <FieldLabel label={COPY.gate} help={COPY.gateHelp} />
              <FieldLabel label={COPY.stayAtLeast} help={COPY.stayAtLeastHelp} />
              <FieldLabel label={COPY.notBefore} help={COPY.notBeforeHelp} />
              <span />
            </div>
            {plan.steps.map((step, index) => (
              <StepRow
                key={index}
                plan={plan}
                step={step}
                index={index}
                isCurrent={showsCurrent && index === plan.currentStep}
              />
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {plan.canAddStep && (
            <Button type="button" variant="outline" onClick={plan.addStep} className={SECONDARY_BUTTON_CLASS}>
              <Plus className="size-3.5" strokeWidth={1.5} />
              {COPY.add}
            </Button>
          )}
          {plan.lockedReason && <span className="supporting">{plan.lockedReason}</span>}
        </div>
        {plan.showsVisitorIdNote && <p className="supporting m-0">{COPY.needsVisitorId}</p>}

        <MarginField plan={plan} />
        <HoldbackField plan={plan} />
      </div>

      <ConfirmDialog
        open={plan.confirmingStart}
        title={plan.planStatus === 'paused' ? COPY.resumeTitle : COPY.startTitle}
        confirmLabel={startLabel}
        disabled={!plan.canStart}
        onConfirm={plan.start}
        onCancel={plan.cancelStart}
      >
        <StartSummary preview={plan.startPreview} />
      </ConfirmDialog>
    </FlagSection>
  );
};

type StartSummaryProps = { preview: PlanStartPreview };

/** Where Start or Resume picks up (resume-flag.ts), and what that does to the rollout now. */
const StartSummary = ({ preview }: StartSummaryProps) => {
  if (preview.kind === 'done') return <p className="supporting m-0">{COPY.pastEvery(percentOf(preview.fromBp))}</p>;
  const { index, total, fromBp, toBp } = preview;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-foreground m-0 text-body">{COPY.startsAt(index + 1, total, percentOf(toBp))}</p>
      <p className="supporting m-0">
        {toBp > fromBp ? COPY.raises(percentOf(fromBp), percentOf(toBp)) : COPY.holds(percentOf(toBp))} {COPY.stepTime}
      </p>
    </div>
  );
};

type StepRowProps = { plan: PlanEditorManager; step: StepDraft; index: number; isCurrent: boolean };

/**
 * One step. A card on a phone, a grid row from lg. The step in force is marked with the
 * selection fill, not a coloured edge.
 */
const StepRow = ({ plan, step, index, isCurrent }: StepRowProps) => {
  const disabled = plan.isLocked;
  const label = `${COPY.step} ${index + 1}`;

  return (
    <div
      className={cn(
        'rounded-card relative grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 border p-2.5',
        'lg:rounded-lg lg:border-transparent lg:px-1 lg:py-1',
        STEP_GRID,
        isCurrent ? 'border-sel-line bg-sel-bg lg:border-sel-line' : 'border-line',
      )}
      aria-current={isCurrent ? 'step' : undefined}
    >
      <span className="num text-dim col-span-2 text-meta lg:col-span-1">
        <span className="lg:hidden">{COPY.step} </span>
        {index + 1}
      </span>

      <label className="grid gap-1 lg:block">
        <span className="micro-label lg:sr-only">{COPY.share}</span>
        <span className="flex items-center gap-1.5">
          <Input
            value={step.percent}
            onChange={(ev) => plan.updateStep(index, { percent: ev.target.value })}
            inputMode="decimal"
            disabled={disabled}
            aria-label={`${label}: ${COPY.share}`}
            className={cn(FIELD_CLASS, 'w-20 text-right font-mono')}
          />
          <span className="text-dim text-body">%</span>
        </span>
      </label>

      <div className="grid gap-1 lg:block">
        <span className="lg:hidden">
          <FieldLabel label={COPY.gate} help={COPY.gateHelp} />
        </span>
        <Select
          value={step.gate}
          onValueChange={(value) => plan.updateStep(index, { gate: value as FlagStepGate })}
          disabled={disabled}
        >
          <SelectTrigger className={cn(FIELD_CLASS, 'w-full')} aria-label={`${label}: ${COPY.gate}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FLAG_STEP_GATES.map((gate) => (
              // The guardrail counts visitors by the website visitor ID; without it only Time.
              <SelectItem key={gate} value={gate} disabled={gate !== 'time' && !plan.guardrailAvailable}>
                {COPY.gates[gate]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="col-span-2 grid gap-1 lg:col-span-1 lg:block">
        <span className="lg:hidden">
          <FieldLabel label={COPY.stayAtLeast} help={COPY.stayAtLeastHelp} />
        </span>
        {/* A guardrail step's time is kept on the page but does not gate it, so it is not shown. */}
        {!usesStepTime(step.gate) ? (
          <span className="supporting flex h-9 items-center">{COPY.guardrailDecides}</span>
        ) : (
          <span className="flex items-center gap-1.5">
            <Input
              value={step.duration}
              onChange={(ev) => plan.updateStep(index, { duration: ev.target.value })}
              inputMode="decimal"
              placeholder="0"
              disabled={disabled}
              aria-label={`${label}: ${COPY.stayAtLeast}`}
              className={cn(FIELD_CLASS, 'w-16 text-right font-mono lg:w-20')}
            />
            <Select
              value={step.unit}
              onValueChange={(value) => plan.updateStep(index, { unit: value as DurationUnit })}
              disabled={disabled}
            >
              <SelectTrigger className={cn(FIELD_CLASS, 'w-24 lg:w-28')} aria-label={`${label}: unit`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">{COPY.hours}</SelectItem>
                <SelectItem value="days">{COPY.days}</SelectItem>
              </SelectContent>
            </Select>
          </span>
        )}
      </div>

      <div className="col-span-2 grid gap-1 lg:col-span-1 lg:block">
        <span className="lg:hidden">
          <FieldLabel label={COPY.notBefore} help={COPY.notBeforeHelp} />
        </span>
        <Input
          type="datetime-local"
          value={step.notBefore}
          onChange={(ev) =>
            plan.updateStep(index, {
              notBefore: ev.target.value,
              // undefined rather than false, so a finished date compares equal to the saved one.
              notBeforeIncomplete: (ev.target.value === '' && ev.target.validity.badInput) || undefined,
            })
          }
          disabled={disabled}
          aria-label={`${label}: ${COPY.notBefore}`}
          className={cn(FIELD_CLASS, 'w-full font-mono text-meta')}
        />
      </div>

      {!disabled ? (
        <button
          type="button"
          onClick={() => plan.removeStep(index)}
          aria-label={`${COPY.remove} ${index + 1}`}
          title={COPY.remove}
          className="text-dim hover:text-danger hover:bg-hover focus-visible:ring-ring absolute top-1 right-1 flex size-9 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:outline-none lg:static lg:justify-self-end"
        >
          <X className="size-4" strokeWidth={1.5} />
        </button>
      ) : (
        <span className="hidden lg:block" />
      )}
    </div>
  );
};

type HoldbackFieldProps = { plan: PlanEditorManager };

/** Editable on a draft only; afterwards the Rollout card states it. */
const HoldbackField = ({ plan }: HoldbackFieldProps) => {
  if (!plan.holdbackEditable) return null;
  return (
    <div className="border-line flex flex-col gap-1.5 border-t pt-3" data-tour="flag-holdback">
      <FieldLabel htmlFor="flag-holdback" label={COPY.holdback} help={COPY.holdbackTip} />
      <div className="flex items-center gap-1.5">
        <Input
          id="flag-holdback"
          value={plan.holdback}
          onChange={(ev) => plan.setHoldback(ev.target.value)}
          inputMode="decimal"
          className={cn(FIELD_CLASS, 'w-20 text-right font-mono')}
        />
        <span className="text-dim text-body">%</span>
      </div>
    </div>
  );
};

type MarginFieldProps = { plan: PlanEditorManager };

/** The guardrail's margin, a relative percent, shown once a step waits on the guardrail. */
const MarginField = ({ plan }: MarginFieldProps) => {
  if (!plan.usesGuardrailGate) return null;
  return (
    <div className="border-line flex flex-col gap-1.5 border-t pt-3">
      <FieldLabel htmlFor="flag-guardrail-margin" label={COPY.margin} help={COPY.marginTip} />
      <div className="flex items-center gap-1.5">
        <Input
          id="flag-guardrail-margin"
          value={plan.margin}
          onChange={(ev) => plan.setMargin(ev.target.value)}
          inputMode="decimal"
          disabled={!plan.marginEditable}
          aria-invalid={plan.marginInvalid}
          className={cn(FIELD_CLASS, 'w-20 text-right font-mono')}
        />
        <span className="text-dim text-body">%</span>
      </div>
      <p className={cn('supporting m-0', plan.marginInvalid && 'text-danger')}>
        {plan.marginInvalid ? COPY.marginInvalid : COPY.marginHelp(plan.marginHelpBp)}
      </p>
    </div>
  );
};
